import {
    ClientInquiryMethod,
    ConsentForm,
    ConsentFormCache,
    DayOption,
    EmailOfficeWindowRequestProps,
    IEmailRequestDetails,
    SignatureData,
    ClientStatus,
    DateFormats,
    IClient,
    IClientBooking,
    IClinician,
    INewOutboundEmail,
    OutboundEmailType,
    OutboundEmailStatus,
    IEmailAppointmentDetails,
    IEmailClient,
    NO_SUPERVISOR_COPY,
    NO_BILLING_COPY,
    ClinicianCredential,
    RecurrenceRule,
    ClinicianOfficeWindowRequests,
    IReservedSchedule,
    IEmailReservedWindowDetails,
} from "intake_shared/src/types";
import {
    calculateFees,
    getApplicableFee,
    IFeeCalculation,
} from "intake_shared/src/fees";
import store from "../store";
import moment from "../utils/app_moment";
import {
    findFeeByPracticeId,
    findLabelByPracticeId,
    getClinicianName,
    toTitleCase,
} from "./utils";
import { v4 } from "uuid";
import { isSameString } from "intake_shared/src/util";
import { getOfficeManagers } from "../store/cache/location";
import { DepartmentCache } from "../store/cache/requested_service";
import { getClientAge, isClientMinor } from "./age";
import { getGuardianFullName } from "./guardian";
import {
    getAppointmentConsentForm,
    getAppointmentConsentFormType,
    getConsentFormLinks,
    getRequiredConsentFormLink,
} from "./consent_form";
import { meetsIntakeSessionClientAndBookingRequirements } from "../v2/v2utils";

const STREET_ADDRESSES: { [key: string]: string } = {
    Brooklyn: "300 Cadman Plaza West",
    Madison: "136 Madison Avenue",
    "Upper East Side": "1199 Park Ave",
};

const SUITE: { [key: string]: string } = {
    "Upper East Side": "1C",
};

function sanitizeParentName(parentName) {
    // Remove anything after a slash or any words like 'Father:', 'Mother:', etc.
    const sanitized = parentName
        .replace(/\/.*|Father:|Mother:|[^a-zA-Z0-9\s-]/g, "")
        .trim();
    return sanitized;
}

function getApptDetails(appointment: IClientBooking): IEmailAppointmentDetails {
    const start_moment = moment(appointment.start);
    const starting_date_moment = moment(appointment.starting_date);

    // Safely handle supervisor intake session with validation
    const supervisorIntakeStartTime =
        appointment.supervisor_intake_session?.start_time;
    const supervisor_intake_start = supervisorIntakeStartTime
        ? moment(supervisorIntakeStartTime)
        : null;

    return {
        id: appointment.id,
        day: start_moment.format(DateFormats.DAY),
        date_str: starting_date_moment.format(DateFormats.DAY_MONTH_YEAR_FORMAT),
        time_str: starting_date_moment.format(DateFormats.HOUR_MIN_FORMAT),
        location: appointment.location || "",
        street: STREET_ADDRESSES[appointment.location || ""] || "",
        suite: SUITE[appointment.location || ""] || "",
        requested_service: appointment.requested_service || "",
        practice_id: appointment.practice_id || "",
        recurrence_rule:
            appointment.recurrenceRule?.toLowerCase()?.replace("-", "") || "",
        supervisor_name:
            appointment.supervisor_intake_session?.clinician_display_name || "",
        intake_session_practice_id:
            appointment.supervisor_intake_session?.practice_id || "",
        supervisor_intake_day:
            supervisor_intake_start?.format(DateFormats.DAY) || "",
        supervisor_intake_date_str:
            supervisor_intake_start?.format(DateFormats.DAY_MONTH_YEAR_FORMAT) || "",
        supervisor_intake_time_str:
            supervisor_intake_start?.format(DateFormats.HOUR_MIN_FORMAT) || "",
    };
}

function getReservedWindowDetails(
    availability: IReservedSchedule
): IEmailReservedWindowDetails {
    const start_moment = moment(availability.start || availability.starting_date);
    const end_moment = moment(availability.end || availability.ending_date);
    return {
        from: start_moment.format(DateFormats.HOUR_MIN_FORMAT),
        to: end_moment.format(DateFormats.HOUR_MIN_FORMAT),
        starting_date: start_moment.format(DateFormats.DAY_MONTH_YEAR_FORMAT),
        recurrence: availability.recurrenceRule || ("" as RecurrenceRule),
    };
}

function getLocationAddress(location_address: string, office?: string) {
    if (!location_address) return "";

    if (office) {
        return `${location_address}, Room ${office}`;
    } else {
        return `${location_address}`;
    }
}

function sendEmailToPrimarySupervisor(
    practiceId: string,
    clinician: IClinician,
    allDepartmentAndServices: DepartmentCache
) {
    // If allDepartmentAndServices or client data is not available, return false
    if (
        !allDepartmentAndServices ||
        !clinician ||
        !clinician.departments ||
        clinician.departments.length === 0
    ) {
        return false;
    }

    // Get the first department from client.departments
    const firstDepartment = clinician.departments[0];

    // Check if the practiceId matches the practice_id of the first department
    const department = allDepartmentAndServices[firstDepartment];
    const isMatch = department && department.practice_id === practiceId;

    return isMatch;
}

function getOfficeWindowRequestDetails(
    request_detail: EmailOfficeWindowRequestProps
): IEmailRequestDetails {
    let categorMessage;
    if (request_detail.category === "request_list") {
        categorMessage = "requested an office window at";
    } else if (request_detail.category === "wait_list") {
        categorMessage = "joined the wailist for";
    } else if (request_detail.category === "delete_office_window_list") {
        categorMessage = "sent a request to delete office window";
    }
    const start_moment = moment(request_detail.start_date);
    const end_date_moment = moment(request_detail.end_date);
    return {
        date_str: start_moment.format(DateFormats.DAY_MONTH_YEAR_FORMAT),
        day: start_moment.format(DateFormats.DAY) as DayOption,
        start_time: start_moment.format(DateFormats.HOUR_MIN_FORMAT),
        end_time: end_date_moment.format(DateFormats.HOUR_MIN_FORMAT),
        location: request_detail.location,
        office: request_detail.office,
        category: categorMessage || "",
    };
}

function getFirstName(client) {
    const isMinor = isClientMinor(client?.date_of_birth);
    const clientParent =
        sanitizeParentName(client.primary_parent) ||
        sanitizeParentName(client.other_parent) ||
        "";
    const adultFirstName = client.preferred_name || client.first_name;
    return isMinor ? clientParent : adultFirstName;
}

function getClientName(client: IClient) {
    return {
        first_name: client.preferred_name || client.first_name,
        last_name: client.last_name,
        preferred_name: client.preferred_name || "N/A",
        primary_parent_guardian_name: getGuardianFullName(
            client.primary_parent,
            client.primary_parent_last_name
        ),
        other_parent_guardian_name: getGuardianFullName(
            client.other_parent,
            client.other_parent_last_name
        ),
    };
}

function addCCs(type: OutboundEmailType) {
    if (type !== OutboundEmailType.VERIFICATION_TEAM_NOTIFY) {
        const cc = [
            {
                email: "support@humantoldmanagement-help.freshdesk.com",
                first_name: "",
            },
        ];
        return cc;
    }
    return [];
}

function getClientEmailProps(
    client: IClient,
    appointment?: IClientBooking,
    consentForm?: ConsentForm,
    supervisor_name?: string,
    clinician_credential?: string
): IEmailClient {
    const self_paid =
        Boolean(client[ClientStatus.FEE_SETTING]?.is_self_pay) ||
        client?.insurance_provider?.toLowerCase() === "self-pay";
    const email_props = {
        ...getClientName(client),
        phone: client.phone_no,
        email: client.email,
        id: client.id,
        pronouns: client.pronouns || "None",
        status: client.status,
        dob: client?.date_of_birth
            ? moment(client.date_of_birth).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
            : null,
        insurance: client?.insurance_provider || "",
        insurance_id: client?.insurance_member_id || "",
        client_concerns: "",
        age: getClientAge(client?.date_of_birth),
        isMinor: isClientMinor(client?.date_of_birth),
        other_parent_guardian_email: client.other_parent_email || "",
        other_parent_guardian_phone: client.other_parent_phone || "",
        has_no_other_parent_guardian: Boolean(client.has_no_other_parent_guardian),
        no_other_parent_guardian_reason:
            client.no_other_parent_guardian_reason || "",
        consent_form_link_adult:
            getConsentFormLinks(consentForm)?.consent_form_link_adult,
        consent_form_link_minor:
            getConsentFormLinks(consentForm)?.consent_form_link_minor,
        consent_form_link_minor_secondary_custodian:
            getConsentFormLinks(consentForm)
                ?.consent_form_link_minor_secondary_custodian,
        single_consent_form_link:
            getConsentFormLinks(consentForm)?.single_consent_form_link,
        patient_portal_url: appointment?.id
            ? `https://client.usemantle.com/?bookingId=${encodeURIComponent(
                  appointment.id
              )}`
            : "https://client.usemantle.com/",
        additional_notes: client[ClientStatus.APPT_SETTING]?.email_notes || "None",
        clinician_display_name: appointment?.clinician_display_name || "",
        supervisor_name: supervisor_name || "",
        valid_credential:
            clinician_credential === "MHC-LP" || clinician_credential === "LMSW",
        is_self_paid: self_paid,
    };
    if (appointment) {
        email_props["client_concerns"] =
            (client?.appointment_checks || {})[
                appointment?.id
            ]?.consult_call_notes?.trim() || "None";
        email_props["appointment_id"] = appointment?.id || "";
        email_props["requested_service"] = appointment?.requested_service || "";
        email_props["is_rechecked"] = Boolean(
            (client?.appointment_checks || {})[appointment?.id]
                ?.consent_form_rechecked
        );
        email_props["supervisor_intake_not_required"] = Boolean(
            (client?.appointment_checks || {})[appointment?.id]
                ?.supervisor_intake_not_required
        );
        email_props["supervisor_intake_not_required_reason"] =
            (client?.appointment_checks || {})[appointment?.id]
                ?.supervisor_intake_not_required_reason || "";

        if (
            appointment.session_attendees?.length &&
            (isSameString(appointment.requested_service, "Couples Therapy") ||
                isSameString(appointment.requested_service, "Family Therapy"))
        ) {
            email_props["partner_attendees"] = appointment.session_attendees
                .filter((a) => a.active !== false && a.guardian_role !== "primary")
                .map((a) => ({
                    first_name: a.first_name,
                    last_name: a.last_name,
                    email: a.email,
                    relationship: a.relationship,
                }));
        }
    }
    return email_props;
}

function getConsentEmailClientProps(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    clinicianCredential = ""
): IEmailClient {
    if (
        !getRequiredConsentFormLink(
            consentForm,
            isClientMinor(client.date_of_birth)
        ).trim()
    ) {
        throw new Error(
            "The selected consent form does not have a link configured for this client."
        );
    }

    const supervisorIntakeNotRequired = Boolean(
        client.appointment_checks?.[appointment.id]?.supervisor_intake_not_required
    );
    const supervisorIntakeRequired =
        !supervisorIntakeNotRequired &&
        meetsIntakeSessionClientAndBookingRequirements(client, appointment) &&
        (clinicianCredential === ClinicianCredential.MHCLP ||
            clinicianCredential === ClinicianCredential.LMSW);

    return {
        ...getClientEmailProps(client, appointment, consentForm),
        is_pnp: clinicianCredential.trim().toUpperCase().startsWith("PNP"),
        clinician_first_name:
            appointment.clinician_display_name?.trim().split(/\s+/)[0] ?? "",
        supervisor_intake_status: supervisorIntakeRequired
            ? appointment.supervisor_intake_session
                ? "booked"
                : "required-not-booked"
            : "not-required",
    };
}

const validateFees = (
    clinician_credential: string,
    appointment: IClientBooking,
    allDepartmentsAndServices: DepartmentCache,
    client: IClient,
    department: string,
    clinicianEmail: string
) => {
    const feeType =
        findFeeByPracticeId(appointment.practice_id, allDepartmentsAndServices) ||
        "";
    const self_paid =
        Boolean(client[ClientStatus.FEE_SETTING]?.is_self_pay) ||
        (clinicianEmail && client.clinician_fees?.[clinicianEmail]?.is_self_pay) ||
        client?.insurance_provider?.toLowerCase() === "self-pay";
    const { fee_to_use, fee_type } = getApplicableFee(
        client,
        clinician_credential || "",
        clinicianEmail,
        appointment?.requested_service,
        feeType
    );
    let fee_description_prefix = "";

    // Department check for Park Hill (PH)
    if (department === "PH") {
        fee_description_prefix = "the agreed upon In Network";
    } else if (
        client.inquiry_method.toLowerCase() ===
            ClientInquiryMethod.ZDOC.toLowerCase() ||
        self_paid
    ) {
        fee_description_prefix = "the agreed upon";
    } else {
        fee_description_prefix = "the agreed upon Out of Network";
    }
    const department_fee_type = department === "PH" ? "INN FEES" : "ONN FEES";

    const fees = calculateFees(fee_to_use, fee_type, department_fee_type);
    let message = `As a reminder, ${fee_description_prefix} `;
    if (!fees) {
        message = "";
    } else if (fees) {
        // KAP structured fee descriptions (Kap Fee)
        if (fee_type === "Kap Fee") {
            const {
                pnp_evaluation_fee,
                therapy_intake_fee,
                kap_initial_fee,
                kap_initial_sessions,
                kap_ongoing_fee,
            } = fees as IFeeCalculation;

            if (
                pnp_evaluation_fee &&
                therapy_intake_fee &&
                kap_initial_fee &&
                kap_initial_sessions &&
                kap_ongoing_fee
            ) {
                return `As a reminder, ${fee_description_prefix} fee schedule is $${pnp_evaluation_fee} for PNP Evaluation; $${therapy_intake_fee} for Therapy Intake; $${kap_initial_fee} for the first ${kap_initial_sessions} KAP sessions, then $${kap_ongoing_fee} per KAP session`;
            }

            if (pnp_evaluation_fee && therapy_intake_fee && kap_ongoing_fee) {
                return `As a reminder, ${fee_description_prefix} fee schedule is $${pnp_evaluation_fee} for PNP Evaluation; $${therapy_intake_fee} for Therapy Intake; $${kap_ongoing_fee} per KAP session`;
            }
        }

        // Handle single psych fee format (e.g., "16" or "16.09")
        if (fees.single_psych_fee) {
            return `As a reminder, ${fee_description_prefix} fee schedule is $${fees.single_psych_fee}/session`;
        }

        // Handle complex fee structure (evaluation + UDM + ongoing)
        if (fees.evaluation_fee || fees.udm_fee || fees.ongoing_fee) {
            // Handle UDM + ongoing format (no evaluation) - e.g., "452 UDM, then 22"
            if (fees.udm_fee && fees.ongoing_fee && !fees.evaluation_fee) {
                return `As a reminder, ${fee_description_prefix} fee schedule is $${fees.udm_fee}/session until your deductible is met, then $${fees.ongoing_fee}/session for ongoing sessions`;
            }

            if (fees.evaluation_fee) {
                // Check if evaluation has multiple sessions (like 165.76x2)
                const evaluation_match = fees.evaluation_fee.match(
                    /^(\d+(?:\.\d+)?)x(\d+)$/
                );
                if (evaluation_match) {
                    const evalAmount = evaluation_match[1];
                    const evalSessions = evaluation_match[2];

                    // If x1, treat as single evaluation
                    if (evalSessions === "1") {
                        message += `fee schedule is $${evalAmount} for your evaluation`;
                    } else {
                        // If x2 or higher, treat as multiple sessions
                        message += `fee schedule is $${evalAmount}/session for your first ${evalSessions} sessions`;
                    }
                } else {
                    message += `fee schedule is $${fees.evaluation_fee} for your evaluation`;
                }
            }

            if (fees.udm_fee) {
                if (message) {
                    message += `, and $${fees.udm_fee}/session until your deductible is met`;
                } else {
                    message += `fee schedule is $${fees.udm_fee}/session until your deductible is met`;
                }
            }

            if (fees.ongoing_fee) {
                if (message) {
                    message += `, then $${fees.ongoing_fee}/session for ongoing sessions`;
                } else {
                    message += `fee schedule is $${fees.ongoing_fee}/session for ongoing sessions`;
                }
            }

            return message;
        }

        // Handle existing fee structures
        const has_temp_fee = fees.temp_fee && fees.temp_no_of_sessions;
        if (has_temp_fee) {
            message += `fee schedule is $${fees.temp_fee}/session for the first ${fees.temp_no_of_sessions} sessions`;
        }

        if (fees.single_fee) {
            message += `${has_temp_fee ? " then" : "fee schedule is"} $${
                fees.single_fee
            }/session`;
        }

        if (fees.tbd_fee) {
            message += `${has_temp_fee ? " then" : "fee schedule is"} $${
                fees.tbd_fee
            }/session until your insurance company responds to our first claim`;
        }
    }

    const isTestingFee =
        fee_type === "Testing Fee" || fee_type === "Intern/Extern Testing Fee";
    if (isTestingFee) {
        message = `As a reminder, ${fee_description_prefix} fee schedule is $${fee_to_use}`;
    }
    if (isTestingFee && Number(fee_to_use) > 500) {
        message +=
            ", $250 of which will be due at time of your intake appointment, the balance can be paid separately or through a payment plan that you can set up with billing. Please note that 50% of your balance must be paid in order to receive your results";
    }

    return message;
};

function getDepartmentDescription(department) {
    switch (department) {
        case "HT":
            return "Humantold";
        case "CHP":
            return "Carnegie Hill Psychiatry";
        case "PH":
            return "Park Hill";
        default:
            return "Humantold";
    }
}

// consent form emails start
interface ConsentEmailOptions {
    isUrgentReminder?: boolean;
    clinicianCredential?: string;
    queueEmail?: (
        email: INewOutboundEmail,
        status: OutboundEmailStatus
    ) => Promise<void>;
}

function getConsentEmailSubject(
    subject: string,
    { isUrgentReminder = false }: ConsentEmailOptions
) {
    return isUrgentReminder ? `${subject} - URGENT REMINDER` : subject;
}

export async function sendHumantoldConsentFormEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.HUMANTOLD_CONSENT_FORM,
        subject: getConsentEmailSubject("Action Required: Humantold Appt", options),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.HUMANTOLD_CONSENT_FORM),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: getConsentEmailClientProps(
            client,
            appointment,
            consentForm,
            options.clinicianCredential
        ),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendCHPExternTherapyConsentFormEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CHP_EXTERN_THERAPY_CONSENT_FORM,
        subject: getConsentEmailSubject(
            "Action Required: Carnegie Hill Therapy Appt",
            options
        ),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.CHP_EXTERN_THERAPY_CONSENT_FORM),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: getConsentEmailClientProps(
            client,
            appointment,
            consentForm,
            options.clinicianCredential
        ),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendCHPTestingConsentFormEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CHP_TESTING_CONSENT_FORM,
        subject: getConsentEmailSubject(
            "Action Required: Mental Health Assessment",
            options
        ),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.CHP_TESTING_CONSENT_FORM),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: getConsentEmailClientProps(
            client,
            appointment,
            consentForm,
            options.clinicianCredential
        ),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendCHPCareerAssessmentFormEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CHP_CAREER_ASSESSMENT_CONSENT_FORM,
        subject: getConsentEmailSubject(
            "Action Required: Carnegie Hill Psychiatry Career Assessment Appt",
            options
        ),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.CHP_CAREER_ASSESSMENT_CONSENT_FORM),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: getConsentEmailClientProps(
            client,
            appointment,
            consentForm,
            options.clinicianCredential
        ),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendKAPConsentFormEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.KAP_CONSENT,
        subject: getConsentEmailSubject(
            "Action Required: Ketamine Assisted Psychotherapy",
            options
        ),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.KAP_CONSENT),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: {
            ...getConsentEmailClientProps(
                client,
                appointment,
                consentForm,
                options.clinicianCredential
            ),
            is_pnp:
                (options.clinicianCredential ?? "").startsWith("PNP") ||
                appointment.requested_service ===
                    "Ketamine Assisted Psychotherapy (Therapy Intake)",
        },
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendCHPNutritionCounselingFormEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CHP_NUTRITION_COUNSELING_CONSENT_FORM,
        subject: getConsentEmailSubject(
            "Action Required: Carnegie Hill Psychiatry Nutrition Counseling Appt",
            options
        ),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.CHP_NUTRITION_COUNSELING_CONSENT_FORM),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: getConsentEmailClientProps(
            client,
            appointment,
            consentForm,
            options.clinicianCredential
        ),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendCHPPsychFormEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CHP_PSYCH_APPT_CONSENT_FORM,
        subject: getConsentEmailSubject(
            "Action Required: Carnegie Hill Psychiatry Appt",
            options
        ),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.CHP_PSYCH_APPT_CONSENT_FORM),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: {
            ...getConsentEmailClientProps(
                client,
                appointment,
                consentForm,
                options.clinicianCredential
            ),
            is_pnp:
                (options.clinicianCredential ?? "").startsWith("PNP") ||
                appointment.requested_service === "Medication Management" ||
                appointment.requested_service === "In-Person Med Management",
        },
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendPH_IN_NetworkConsentFormEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.PH_IN_NETWORK_CONSENT_FORM,
        subject: getConsentEmailSubject("Action Required: Park Hill Appt", options),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.PH_IN_NETWORK_CONSENT_FORM),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: {
            ...getConsentEmailClientProps(
                client,
                appointment,
                consentForm,
                options.clinicianCredential
            ),
            is_pnp:
                (options.clinicianCredential ?? "").startsWith("PNP") ||
                appointment.requested_service ===
                    "Ketamine Assisted Psychotherapy (Therapy Intake)",
        },
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendPHNutritionCounselingConsentFormEmail(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm | undefined,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.PH_NUTRITION_COUNSELING_CONSENT_FORM,
        subject: getConsentEmailSubject(
            "Action Required: Park Hill Nutrition Counseling Appt",
            options
        ),
        preview_text: "",
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        signature: signature || {},
        cc: [
            ...addCCs(OutboundEmailType.PH_NUTRITION_COUNSELING_CONSENT_FORM),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: getConsentEmailClientProps(
            client,
            appointment,
            consentForm,
            options.clinicianCredential
        ),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await (options.queueEmail ?? store.queueEmail)(
        client_email,
        OutboundEmailStatus.PENDING
    );
}

export async function sendAppointmentConsentFormEmail(
    client: IClient,
    appointment: IClientBooking,
    appointmentPractice: string,
    allConsentForms: ConsentFormCache,
    signature: SignatureData,
    options: ConsentEmailOptions = {}
): Promise<ConsentForm | undefined> {
    const form = getAppointmentConsentForm(
        appointmentPractice,
        appointment,
        allConsentForms
    );

    if (!form?.id) {
        return;
    }

    const consentFormType = getAppointmentConsentFormType(
        appointmentPractice,
        appointment
    );
    const emailOptions: ConsentEmailOptions = {
        ...options,
        queueEmail: (email, status) =>
            (options.queueEmail ?? store.queueEmail)(
                consentFormType
                    ? { ...email, consent_form_type: consentFormType }
                    : email,
                status
            ),
    };

    if (form.id === allConsentForms.HUMANTOLD_CONSENT_FORM?.id) {
        await sendHumantoldConsentFormEmails(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else if (form.id === allConsentForms.PH_IN_NETWORK_CONSENT_FORM?.id) {
        await sendPH_IN_NetworkConsentFormEmails(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else if (
        form.id === allConsentForms.PH_NUTRITION_COUNSELING_CONSENT_FORM?.id
    ) {
        await sendPHNutritionCounselingConsentFormEmail(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else if (form.id === allConsentForms.CHP_TESTING_CONSENT_FORM?.id) {
        await sendCHPTestingConsentFormEmails(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else if (form.id === allConsentForms.KAP_CONSENT?.id) {
        await sendKAPConsentFormEmails(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else if (form.id === allConsentForms.CHP_PSYCH_APPT_CONSENT_FORM?.id) {
        await sendCHPPsychFormEmails(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else if (
        form.id === allConsentForms.CHP_NUTRITION_COUNSELING_CONSENT_FORM?.id
    ) {
        await sendCHPNutritionCounselingFormEmails(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else if (form.id === allConsentForms.CHP_EXTERN_THERAPY_CONSENT_FORM?.id) {
        await sendCHPExternTherapyConsentFormEmails(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else if (form.id === allConsentForms.CHP_CAREER_ASSESSMENT_CONSENT_FORM?.id) {
        await sendCHPCareerAssessmentFormEmails(
            client,
            appointment,
            form,
            signature,
            emailOptions
        );
    } else {
        return;
    }

    return form;
}

const CC_AUTH_TYPE_BY_DEPARTMENT: Record<string, string> = {
    HT: "Humantold Appts",
    PH: "Park Hill Appts",
    CHP: "CHP Appts",
};

function getCCAuthLink(ccAuthForm: ConsentForm, department: string): string {
    const typeName = CC_AUTH_TYPE_BY_DEPARTMENT[department];
    const match = ccAuthForm.types?.find((t) => t.name === typeName);
    if (match?.link) return match.link;
    // Fall back to a blank-named type (original single-link format)
    const fallback = ccAuthForm.types?.find((t) => !t.name);
    return fallback?.link ?? "";
}

export async function sendConsentErrorConsentEmails(
    client: IClient,
    appointment: IClientBooking,
    consentForm: ConsentForm,
    department: string,
    signature: SignatureData
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);

    let subject = "Action Required: Humantold Consent Error";
    if (department === "PH") {
        subject = "Action Required: Park Hill Consent Error";
    }
    if (department === "CHP") {
        subject = "Action Required: Carnegie Hill Consent Error";
    }

    const ccAuthLink = getCCAuthLink(consentForm, department);
    const clientEmailProps = getClientEmailProps(client, appointment);

    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CONSENT_ERROR_CC_AUTH_ONLY,
        subject: subject,
        preview_text: "",
        signature: signature || {},
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        cc: [
            ...addCCs(OutboundEmailType.CONSENT_ERROR_CC_AUTH_ONLY),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: {
            ...clientEmailProps,
            single_consent_form_link: ccAuthLink,
        },
        clinician_name: clientEmailProps.clinician_display_name,
    };
    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendConsentIssueCancelEmails(
    client: IClient,
    appointment: IClientBooking,
    department: string,
    signature: SignatureData
) {
    const appt_details = getApptDetails(appointment);
    const firstName = getFirstName(client);

    let subject = "Humantold Appt Canceled";
    if (department === "PH") {
        subject = "Park Hill Psychiatry Appt Canceled";
    }
    if (department === "CHP") {
        subject = "Carnegie Hill Psychiatry Appt Canceled";
    }

    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CONSENT_ISSUE_CANCEL,
        subject: subject,
        preview_text: "",
        signature: signature || {},
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        cc: [
            ...addCCs(OutboundEmailType.CONSENT_ISSUE_CANCEL),
            {
                email: "billing@humantold.com",
                first_name: "",
            },
        ],
        appointment_details: {
            ...appt_details,
        },
        client: getClientEmailProps(client, appointment),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendCollateralConsentFormEmail({
    client,
    appointment,
    attendeeId,
    attendeeFirstName,
    attendeeEmail,
    attendeeRelationship,
    attendeeIsMinor,
    consentForm,
    signature,
    department,
    isCustodian,
    isReminder,
}: {
    client: IClient;
    appointment: IClientBooking;
    attendeeId: string;
    attendeeFirstName: string;
    attendeeEmail: string;
    attendeeRelationship?: string;
    attendeeIsMinor?: boolean;
    consentForm: ConsentForm | undefined;
    signature: SignatureData;
    department?: string;
    isCustodian?: boolean;
    isReminder?: boolean;
}) {
    const links = getConsentFormLinks(consentForm);

    let collateralLink: string;
    if (isCustodian) {
        collateralLink = links.consent_form_link_minor_secondary_custodian;
    } else {
        collateralLink = attendeeIsMinor
            ? links.consent_form_link_collateral_minor
            : links.consent_form_link_collateral_adult;
    }

    if (!collateralLink) {
        const linkType = isCustodian
            ? "Minors - Secondary Custodian"
            : attendeeIsMinor
            ? "Collateral Consent MINOR"
            : "Collateral Consent";
        console.info(
            `[sendCollateralConsentFormEmail] No "${linkType}" link configured for attendee ${attendeeId} (appointmentId: ${appointment.id})`
        );
        throw new Error(
            `No "${linkType}" link configured for this attendee. Please ensure the consent form has the required link set up.`
        );
    }

    const clientName = [client.first_name, client.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
    const clinicianName = getClientEmailProps(
        client,
        appointment
    ).clinician_display_name;

    const email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.COLLATERAL_CONSENT_FORM,
        subject: isReminder
            ? "Action Required: Session Consent Form - URGENT REMINDER"
            : "Action Required: Session Consent Form",
        preview_text: "",
        recipient: {
            email: attendeeEmail,
            first_name: attendeeFirstName,
        },
        signature: signature || {},
        cc: [],
        client: {
            ...getClientEmailProps(client, appointment, consentForm),
            consent_form_link_adult: isCustodian
                ? links.consent_form_link_minor_secondary_custodian
                : links.consent_form_link_collateral_adult,
            consent_form_link_minor: isCustodian
                ? links.consent_form_link_minor_secondary_custodian
                : links.consent_form_link_collateral_minor,
        },
        clinician_name: clinicianName,
        client_name: clientName,
        attendee_relationship: attendeeRelationship || "",
        attendee_name: attendeeFirstName,
        attendee_is_minor: Boolean(attendeeIsMinor),
        practice_name: getDepartmentDescription(department || "HT"),
    };

    await store.queueEmail(email, OutboundEmailStatus.PENDING);
}

// consent form emails end

export async function sendAppointmentHeldEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    clientRequestedCall: boolean,
    signature: SignatureData
) {
    if (clientRequestedCall) {
        const clinician_email: INewOutboundEmail = {
            clinician_id: clinician.id,
            client_id: client.id,
            recipient_type: "clinician",
            type: OutboundEmailType.APPT_HELD_CLINICIAN_CONSULT,
            subject: "Action Needed: Consult call",
            signature: signature || ({} as SignatureData),
            preview_text:
                "We are holding a timeslot for a potential client who requested a consultation call prior to the appt.",
            recipient: {
                email: clinician.email,
                first_name: getClinicianName(clinician),
            },
            cc: addCCs(OutboundEmailType.APPT_HELD_CLINICIAN_CONSULT),
            client: getClientEmailProps(client, appointment),
        };
        await store.queueEmail(clinician_email, OutboundEmailStatus.PENDING);
    }
}

export async function sendClientTerminationEmails(
    client: IClient,
    appointment: IClientBooking,
    signature: SignatureData
) {
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CLIENT_TERMINATION_EMAIL,
        subject: `We are sorry to see you go!`,
        preview_text: "",
        signature: signature || ({} as SignatureData),
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        cc: addCCs(OutboundEmailType.CLIENT_TERMINATION_EMAIL),
        client: getClientEmailProps(client, appointment),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
    };
    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendClinicianReservedWindowEmails(
    clincian_name: string,
    client_email: string,
    availability: IReservedSchedule,
    signature?: SignatureData
) {
    const reserved_window_details = getReservedWindowDetails(availability);

    const internal_email: INewOutboundEmail = {
        recipient_type: "internal",
        type: OutboundEmailType.CLINICIAN_RESERVED_TIME_WINDOW,
        subject: `A Clincian Reserved a Window For a Client`,
        preview_text: "",
        signature: signature || ({} as SignatureData),
        recipient: {
            email: "intake@humantold.com",
            first_name: "Intake",
        },
        no_cc_internal: true,
        cc: addCCs(OutboundEmailType.CLINICIAN_RESERVED_TIME_WINDOW),
        client_email: client_email,
        reserved_window_details: {
            ...reserved_window_details,
        },
        clinician_name: clincian_name,
    };
    await store.queueEmail(internal_email, OutboundEmailStatus.PENDING);
}

export async function sendEmailCreatedFromSendGrid(
    client: IClient,
    appointment: IClientBooking,
    subject: string,
    templateId: string
) {
    const firstName = getFirstName(client);
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.CREATED_FROM_SEND_GRID,
        subject: subject,
        templateId: templateId,
        preview_text: "",
        signature: {} as SignatureData,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        cc: [],
        client: getClientEmailProps(client, appointment),
    };
    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendRequestedOfficeWindowEmails(
    office_request_details: ClinicianOfficeWindowRequests,
    intakeSignature: SignatureData
) {
    const getOfficeManager = await getOfficeManagers();
    const officeManagerEmail = getOfficeManager[office_request_details.location];

    //  Filter out "intake@humantold.com" from the emails if it exists
    const filteredOfficeManagerEmails = officeManagerEmail.filter(
        (email) => email !== "intake@humantold.com"
    );

    if (!filteredOfficeManagerEmails || filteredOfficeManagerEmails.length === 0) {
        return;
    }
    const officeManagers = filteredOfficeManagerEmails.map((email) => ({
        email,
        first_name: "",
    }));

    const request_details = getOfficeWindowRequestDetails(office_request_details);
    const office_manager_email: INewOutboundEmail = {
        recipient_type: "office_manager",
        type: OutboundEmailType.REQUESTED_OFFICE_WINDOW,
        subject:
            office_request_details.category === "delete_office_window_list"
                ? "Delete Office Window Request"
                : "Office Window Request",
        preview_text:
            office_request_details.category === "delete_office_window_list"
                ? "A clinician has sent a request to delete office window"
                : "A clinician has requested an office window.",
        recipient: officeManagers[0],
        cc: officeManagers,
        signature: intakeSignature || ({} as SignatureData),
        no_cc_internal: true,
        office_request_details: {
            ...request_details,
            clinician_name: office_request_details.clinician_name,
            reason: office_request_details.reason_for_delete
                ? `Reason: ${office_request_details.reason_for_delete}`
                : "",
        },
    };

    await store.queueEmail(office_manager_email, OutboundEmailStatus.PENDING);
}

export async function sendOfficeWindowRequestActionEmails(
    clinician_id: string,
    office_request_details: EmailOfficeWindowRequestProps,
    action: string,
    signature: SignatureData
) {
    const request_details = getOfficeWindowRequestDetails(office_request_details);
    const approveRequest = ["Approved", "Approved with changes"].includes(action);

    const clinician_email: INewOutboundEmail = {
        recipient_type: "clinician",
        type: OutboundEmailType.OFFICE_WINDOW_REQUEST_ACTION,
        subject: `Action on your office window request`,
        preview_text: `Your office window request`,
        cc: approveRequest
            ? [
                  {
                      email: "intake@humantold.com",
                      first_name: "",
                  },
                  {
                      email: "ruby@humantold.com",
                      first_name: "",
                  },
              ]
            : [],
        signature: signature || ({} as SignatureData),
        recipient: {
            email: clinician_id,
            first_name: "",
        },
        office_request_details: {
            ...request_details,
            action: action,
            reason: office_request_details.reason
                ? `Reason: ${office_request_details.reason}`
                : "",
        },
    };
    await store.queueEmail(clinician_email, OutboundEmailStatus.PENDING);
}

export async function sendOfficeWindowDeleteRequestActionEmails(
    clinician_id: string,
    office_request_details: EmailOfficeWindowRequestProps,
    action: string,
    signature: SignatureData
) {
    const request_details = getOfficeWindowRequestDetails(office_request_details);
    const clinician_email: INewOutboundEmail = {
        recipient_type: "clinician",
        type: OutboundEmailType.OFFICE_WINDOW_DELETE_REQUEST_ACTION,
        subject: `Action on your office window delete request`,
        preview_text: `Your office window delete request`,
        cc: [],
        signature: signature || ({} as SignatureData),
        recipient: {
            email: clinician_id,
            first_name: "",
        },
        office_request_details: {
            ...request_details,
            action: action,
            reason: office_request_details.reason
                ? `Reason: ${office_request_details.reason}`
                : "",
        },
    };
    await store.queueEmail(clinician_email, OutboundEmailStatus.PENDING);
}

export async function sendDeletedOfficeWindowActionEmails(
    clinician_id: string,
    deleted_office_details: EmailOfficeWindowRequestProps,
    deletedBy: "office_manager" | "clinician",
    signature: SignatureData
) {
    const getOfficeManager = await getOfficeManagers();
    const officeManagerEmail = getOfficeManager[deleted_office_details.location];

    //  Filter out "intake@humantold.com" from the emails if it exists
    const filteredOfficeManagerEmails = officeManagerEmail.filter(
        (email) => email !== "intake@humantold.com"
    );

    if (!filteredOfficeManagerEmails || filteredOfficeManagerEmails.length === 0) {
        return;
    }
    const officeManagers = filteredOfficeManagerEmails.map((email) => ({
        email,
        first_name: "",
    }));

    const request_details = getOfficeWindowRequestDetails(deleted_office_details);
    const deleted_window_email: INewOutboundEmail = {
        recipient_type: deletedBy === "clinician" ? "clinician" : "office_manager",
        type: OutboundEmailType.DELETED_OFFICE_WINDOWS,
        subject: `Office Window Deleted`,
        preview_text: `An Office window has been deleted`,
        cc:
            deletedBy === "clinician"
                ? officeManagers
                : [
                      {
                          email: "ruby@humantold.com",
                          first_name: "",
                      },
                      {
                          email: "office@humantold.com",
                          first_name: "",
                      },
                      {
                          email: "intake@humantold.com",
                          first_name: "",
                      },
                  ],
        no_cc_internal: true,
        signature: signature || ({} as SignatureData),
        recipient:
            deletedBy === "clinician"
                ? officeManagers[0]
                : {
                      email: clinician_id,
                      first_name: "",
                  },
        deleted_office_window: {
            ...request_details,
            deletedBy: deletedBy,
            clinician_name: deleted_office_details.clinician_name,
            reason: deleted_office_details.reason
                ? `Reason: ${deleted_office_details.reason}`
                : "",
        },
    };
    await store.queueEmail(deleted_window_email, OutboundEmailStatus.PENDING);
}
export function isAddressInState(address: string) {
    if (!address) {
        return false;
    }
    return /ny$|new york/i.test(address);
}

export function ignoreSupervisorCc(client: IClient, clinician: IClinician) {
    // Intern-created self-pay must not suppress the assigned supervisor CC
    if (
        clinician.credential === ClinicianCredential.INTERN &&
        clinician.supervisor_id
    ) {
        return false;
    }

    const self_paid = Boolean(client[ClientStatus.FEE_SETTING]?.is_self_pay);

    const no_insurance_copy = NO_SUPERVISOR_COPY.map((i) =>
        i.toLowerCase()
    ).includes(client.insurance_provider.toLowerCase());
    return self_paid || no_insurance_copy;
}

function ignoreBillingCc(insurance_provider: string) {
    return NO_BILLING_COPY.map((i) => i.toLowerCase()).includes(
        insurance_provider.toLowerCase()
    );
}

// APPOINTMENT CONFIRMED EMAILS START
export async function sendAppointmentConfirmedEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    allDepartmentAndServices: DepartmentCache,
    signature: SignatureData,
    locationAddress: string
) {
    const appt_details = getApptDetails(appointment);
    const isRemote =
        isSameString(appointment.location, "Remote") ||
        isSameString(appointment.requested_service, "Teletherapy") ||
        isSameString(appointment.service_delivery_option, "remote");
    const isInperson = !isRemote;
    const isMentalHealthAssessment =
        isSameString(
            appointment.requested_service,
            "Mental Health Assessment: Fully In-Person"
        ) ||
        isSameString(
            appointment.requested_service,
            "Mental Health Assessment: Partial In-Person"
        ) ||
        isSameString(
            appointment.requested_service,
            "Mental Health Assessment: Virtual"
        );
    const isEvaluation = isSameString(appointment.requested_service, "Evaluation");
    const isKetamineAssistedPsychotherapy =
        isSameString(
            appointment.requested_service,
            "Ketamine Assisted Psychotherapy"
        ) ||
        isSameString(
            appointment.requested_service,
            "Ketamine Assisted Psychotherapy (Evaluation)"
        ) ||
        isSameString(
            appointment.requested_service,
            "Ketamine Assisted Psychotherapy (Therapy Intake)"
        );

    const recurrence = appointment.recurrenceRule?.toUpperCase() || "";
    let subject = "";
    if (isMentalHealthAssessment) {
        subject = `Mental Health Assessment Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`;
    } else if (isKetamineAssistedPsychotherapy) {
        subject = `Ketamine Assisted Psychotherapy Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`;
    } else if (isRemote) {
        subject = `Teletherapy Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`;
    } else if (isInperson) {
        subject = `In-Person Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`;
    }

    const appointmentPracticeId =
        appointment.practice_id ||
        (process.env.REACT_APP_MANTLE_PRACTICE_ID as string);

    // get department name by practice_id
    const department = findLabelByPracticeId(
        appointmentPracticeId,
        allDepartmentAndServices
    );

    const clinician_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "clinician",
        type: OutboundEmailType.APPT_CONFIRMED_CLINICIAN,
        recipient: {
            email: clinician.email,
            first_name: getClinicianName(clinician),
        },
        client: getClientEmailProps(client, appointment),
        signature: signature || {},
        subject,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department as string,
                clinician.email
            ),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: [
            ...addCCs(OutboundEmailType.APPT_CONFIRMED_CLINICIAN),
            {
                email: "ruby@humantold.com",
                first_name: "Ruby",
            },
        ],
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${client.first_name}${client.last_name}`,
        ].join(" "),
    };

    if (isEvaluation || isMentalHealthAssessment) {
        clinician_email.cc?.push({
            email: "andrea@humantold.com",
            first_name: "",
        });
        clinician_email.cc?.push({
            email: "kristina@humantold.com",
            first_name: "",
        });
        clinician_email.cc?.push({
            email: "kimberly@humantold.com",
            first_name: "",
        });
    }
    if (client.address && !isAddressInState(client.address)) {
        // TODO: move this to env var.
        clinician_email.cc?.push({
            email: "r.landman@humantold.com",
            first_name: "",
        });
    }

    if (client.insurance_provider && !ignoreBillingCc(client.insurance_provider)) {
        clinician_email.cc?.push({
            email: "billing@humantold.com",
            first_name: "",
        });
    }

    // check to know if supervisors should be cc'd
    const shouldAddSupervisorToCc =
        !ignoreSupervisorCc(client, clinician) ||
        (ignoreSupervisorCc(client, clinician) && department === "PH");

    // check if only primary supervisor should be cc'd
    const isPrimarySupervisor = sendEmailToPrimarySupervisor(
        appointmentPracticeId,
        clinician,
        allDepartmentAndServices
    );

    if (clinician.supervisor_id) {
        if (shouldAddSupervisorToCc && isPrimarySupervisor) {
            clinician_email.cc?.push({
                email: clinician.supervisor_id,
                first_name: "",
            });
        }
    }

    // check if only secondary supervisor should be cc'd
    if (clinician.secondary_supervisor_ids) {
        const findSupervisor = (practice_id) => {
            const supervisor = clinician.secondary_supervisor_ids?.find(
                (item) => item.practice_id === practice_id
            );
            return supervisor ? supervisor.supervisor_id : "";
        };

        if (shouldAddSupervisorToCc) {
            const supervisor_id = findSupervisor(appointmentPracticeId);
            if (supervisor_id) {
                clinician_email.cc = clinician_email.cc || [];
                clinician_email.cc.push({
                    email: supervisor_id,
                    first_name: "",
                });
            }
        }
    }

    if (
        isSameString(appointment.requested_service, "Mental Health Assessment") ||
        isSameString(appointment.requested_service, "Medication Management") ||
        isSameString(appointment.requested_service, "Psychiatric Counseling")
    ) {
        clinician_email.cc?.push({
            email: "a.reilly@humantold.com",
            first_name: "",
        });
    }

    await store.queueEmail(clinician_email, OutboundEmailStatus.PENDING);
    await store.addEmailReminder({
        ...clinician_email,
        id: v4(),
        next_send_time: moment().add(48, "hour").startOf("day").toISOString(),
        max_reminder_count: 3,
        current_reminder_count: 0,
        is_completed: false,
    });
}

export async function sendAppointmentConfirmedHTTeletherapyClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_HT_TELETHERAPY,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Teletherapy Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department as string,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_HT_TELETHERAPY),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedPHTeletherapyClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_PH_TELETHERAPY,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Teletherapy Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department as string,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_PH_TELETHERAPY),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedCHPTeletherapyClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_TELETHERAPY,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Teletherapy Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department as string,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_TELETHERAPY),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedHTInPersonClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_HT_IN_PERSON,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `In-Person Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_HT_IN_PERSON),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedPHInPersonClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_PH_IN_PERSON,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `In-Person Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_PH_IN_PERSON),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedCHPInPersonClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_IN_PERSON,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `In-Person Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_IN_PERSON),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedExternTherapyClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_EXTERN_THERAPY,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Teletherapy Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_EXTERN_THERAPY),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedTestingClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_TESTING,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Mental Health Assessment Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_TESTING),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedKAPClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_KETAMINE,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Ketamine Assisted Psychotherapy Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_KETAMINE),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedCareerAssessmentClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_CAREER_ASSESSMENT,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Career Assessment Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_CAREER_ASSESSMENT),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedNutritionCounselingClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_NUTRITION_COUNSELING,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Nutrition Counseling Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_NUTRITION_COUNSELING),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedPnpTherapyClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    form: ConsentForm,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const recurrence =
        appointment.recurrenceRule === RecurrenceRule.BIWEEKLY ? "BIWEEKLY" : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_PNP_THERAPY,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            form as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Teletherapy Appt Confirmed: ${recurrence} ${appt_details.day}, ${appt_details.date_str}, ${appt_details.time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_CHP_PNP_THERAPY),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: clinician.contact_info_client || "",
        clinician_email: clinician.email || "",
        preview_text: [
            `You are confirmed for ${appt_details.day}, beginning ${appt_details.date_str} @ ${appt_details.time_str} with ${clinician.fullname}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendAppointmentConfirmedSupervisorIntakeClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    supervisor_contact_info_client: string,
    supervisor_email: string,
    supervisor_name?: string
) {
    const firstName = getFirstName(client);
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "patient",
        type: OutboundEmailType.APPT_CONFIRMED_CLIENT_SUPERVISOR_INTAKE,
        recipient: {
            email: client.email,
            first_name: firstName,
        },
        client: getClientEmailProps(
            client,
            appointment,
            {} as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Intake Appt Confirmed: ${appt_details.supervisor_intake_day}, ${appt_details.supervisor_intake_date_str}, ${appt_details.supervisor_intake_time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLIENT_SUPERVISOR_INTAKE),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_contact_info_client: supervisor_contact_info_client || "",
        clinician_email: supervisor_email || "",
        preview_text: [
            `You are confirmed for ${appt_details.supervisor_intake_day}, beginning ${appt_details.supervisor_intake_date_str} @ ${appt_details.supervisor_intake_time_str} with ${appt_details.supervisor_name}`,
        ].join(" "),
    };

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

type EmailRecipient = { email: string; first_name: string };

export function buildSupervisorIntakeCCs(
    clinician: IClinician,
    appointment: IClientBooking,
    allDepartmentAndServices: DepartmentCache
): EmailRecipient[] {
    const intakeSupervisorId =
        appointment.supervisor_intake_session?.supervisor_id || "";
    const appointmentPracticeId =
        appointment.supervisor_intake_session?.practice_id ||
        appointment.practice_id ||
        (process.env.REACT_APP_MANTLE_PRACTICE_ID as string);

    const ccs: EmailRecipient[] = [
        { email: clinician.email, first_name: getClinicianName(clinician) },
    ];

    const isPrimary = sendEmailToPrimarySupervisor(
        appointmentPracticeId,
        clinician,
        allDepartmentAndServices
    );

    if (
        clinician.supervisor_id &&
        isPrimary &&
        clinician.supervisor_id !== intakeSupervisorId
    ) {
        ccs.push({ email: clinician.supervisor_id, first_name: "" });
    }

    if (clinician.secondary_supervisor_ids) {
        const secondarySupervisor = clinician.secondary_supervisor_ids.find(
            (item) => item.practice_id === appointmentPracticeId
        );
        if (
            secondarySupervisor?.supervisor_id &&
            secondarySupervisor.supervisor_id !== intakeSupervisorId
        ) {
            ccs.push({ email: secondarySupervisor.supervisor_id, first_name: "" });
        }
    }

    const seen = new Set<string>();
    return ccs.filter(({ email }) => {
        if (seen.has(email)) return false;
        seen.add(email);
        return true;
    });
}

export async function sendAppointmentConfirmedClinicianSupervisorIntakeClientEmails(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    signature: SignatureData,
    department: string,
    allDepartmentAndServices: DepartmentCache,
    locationAddress: string,
    supervisor_name?: string
) {
    const appt_details = getApptDetails(appointment);
    const supervisor =
        clinician.credential === "MHC-LP" || clinician.credential === "LMSW"
            ? supervisor_name
            : "";
    const client_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "clinician",
        type: OutboundEmailType.APPT_CONFIRMED_CLINICIAN_SUPERVISOR_INTAKE,
        recipient: {
            email: appointment.supervisor_intake_session?.supervisor_id || "",
            first_name:
                appointment.supervisor_intake_session?.clinician_display_name || "",
        },
        client: getClientEmailProps(
            client,
            appointment,
            {} as ConsentForm,
            supervisor,
            clinician.credential
        ),
        signature: signature || {},
        subject: `Intake Appt Confirmed: ${appt_details.supervisor_intake_day}, ${appt_details.supervisor_intake_date_str}, ${appt_details.supervisor_intake_time_str}`,
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department,
                clinician.email
            ),
            department: getDepartmentDescription(department),
            location_address: getLocationAddress(
                locationAddress,
                appointment?.office || ""
            ),
        },
        cc: addCCs(OutboundEmailType.APPT_CONFIRMED_CLINICIAN_SUPERVISOR_INTAKE),
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        clinician_email: clinician.email,
        preview_text: [
            `You are confirmed for ${appt_details.supervisor_intake_day}, beginning ${appt_details.supervisor_intake_date_str} @ ${appt_details.supervisor_intake_time_str} with ${appt_details.supervisor_name}`,
        ].join(" "),
    };

    const additionalCCs = buildSupervisorIntakeCCs(
        clinician,
        appointment,
        allDepartmentAndServices
    );
    additionalCCs.forEach((cc) => client_email.cc?.push(cc));

    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendReturningClientAppointmentEmail(
    client: IClient,
    clinician: IClinician,
    allDepartmentAndServices: DepartmentCache,
    department: string
) {
    const appt_details = getApptDetails(client.confirmed_bookings[0]);
    const fee_description = validateFees(
        clinician.credential,
        appt_details as unknown as IClientBooking,
        allDepartmentAndServices,
        client,
        department,
        clinician.email
    );
    const clinician_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        recipient_type: "clinician",
        type: OutboundEmailType.RETURNING_CLINICIAN,
        subject: `Returning Client ${client.first_name} ${client.last_name}`,
        client: getClientEmailProps(client),
        preview_text: [
            `Your previous client ${client.first_name} ${client.last_name} would like to return for services.`,
            `Their paperwork has been updated and they have been advised to contact you directly for scheduling.`,
        ].join(" "),
        cc: addCCs(OutboundEmailType.RETURNING_CLINICIAN),
        signature: {} as SignatureData,
        recipient: {
            email: clinician.email,
            first_name: clinician.firstname,
            last_name: clinician.lastname,
        },
        appointment_details: { ...appt_details, fee_description },
    };
    if (clinician.supervisor_id) {
        clinician_email.cc = [{ email: clinician.supervisor_id, first_name: "" }];
    }
    await store.queueEmail(clinician_email, OutboundEmailStatus.PENDING);
}

export async function sendAssignClientEmail(
    client: IClient,
    assignee_email: string,
    assignment_notes: string,
    signature: SignatureData
) {
    const client_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "internal",
        type: OutboundEmailType.CLIENT_ASSIGNED,
        subject: `Client assignment: ${client.first_name} ${client.last_name}`,
        preview_text: `A client has been assigned to you.`,
        client: getClientEmailProps(client),
        cc: [],
        signature: signature || ({} as SignatureData),
        no_cc_internal: true,
        assignment_notes,
        recipient: {
            email: assignee_email,
            first_name: "",
            last_name: "",
        },
    };
    await store.queueEmail(client_email, OutboundEmailStatus.PENDING);
}

export async function sendVerificationTeamNotification(
    client: IClient,
    signature: SignatureData
) {
    const resolvedVerificationEmail =
        process.env.REACT_APP_VERIFICATION_TEAM_ADDR || "verification@humantold.com";

    const verification_email: INewOutboundEmail = {
        client_id: client.id,
        recipient_type: "internal",
        type: OutboundEmailType.VERIFICATION_TEAM_NOTIFY,
        subject: `New client on queue`,
        preview_text: `A client has been added to the verification queue`,
        client: getClientEmailProps(client),
        no_cc_internal: true,
        signature: signature || ({} as SignatureData),
        cc: addCCs(OutboundEmailType.VERIFICATION_TEAM_NOTIFY),
        recipient: {
            email: resolvedVerificationEmail,
            first_name: "Verification team",
            last_name: "",
        },
    };
    await store.queueEmail(verification_email, OutboundEmailStatus.PENDING);
}

export async function sendServiceTypeChangeEmail(
    client: IClient,
    clinician: IClinician,
    appointment: IClientBooking,
    allDepartmentAndServices: DepartmentCache,
    new_requested_service: string
) {
    const appt_details = getApptDetails(appointment);

    const appointmentPracticeId =
        appointment.practice_id ||
        (process.env.REACT_APP_MANTLE_PRACTICE_ID as string);

    // get department name by practice_id
    const department = findLabelByPracticeId(
        appointmentPracticeId,
        allDepartmentAndServices
    );

    const clinician_email: INewOutboundEmail = {
        clinician_id: clinician.id,
        client_id: client.id,
        recipient_type: "clinician",
        type: OutboundEmailType.CLINICIAN_REQUESTED_SERVICE_TYPE_CHANGE,
        recipient: {
            email: "intake@humantold.com",
            first_name: "Intake",
        },
        client: getClientEmailProps(client, appointment),
        signature: {} as SignatureData,
        subject: "A Clinician Requested a Service Type Change For a Client",
        appointment_details: {
            ...appt_details,
            fee_description: validateFees(
                clinician.credential,
                appointment,
                allDepartmentAndServices,
                client,
                department as string,
                clinician.email
            ),
            new_requested_service: toTitleCase(new_requested_service || "")?.replace(
                /[_-]/g,
                " "
            ),
        },
        clinician_name: getClientEmailProps(client, appointment)
            .clinician_display_name,
        cc: [
            ...addCCs(OutboundEmailType.APPT_CONFIRMED_CLINICIAN),
            {
                email: clinician.email,
                first_name: clinician.firstname,
            },
        ],
        preview_text: [
            `A clinician requested a service type change for a client`,
        ].join(" "),
    };

    const isPrimarySupervisor = sendEmailToPrimarySupervisor(
        appointmentPracticeId,
        clinician,
        allDepartmentAndServices
    );

    if (clinician.supervisor_id) {
        if (isPrimarySupervisor) {
            clinician_email.cc?.push({
                email: clinician.supervisor_id,
                first_name: "",
            });
        }
    }

    // check if only secondary supervisor should be cc'd
    if (clinician.secondary_supervisor_ids) {
        const findSupervisor = (practice_id) => {
            const supervisor = clinician.secondary_supervisor_ids?.find(
                (item) => item.practice_id === practice_id
            );
            return supervisor ? supervisor.supervisor_id : "";
        };

        const supervisor_id = findSupervisor(appointmentPracticeId);
        if (supervisor_id) {
            clinician_email.cc = clinician_email.cc || [];
            clinician_email.cc.push({
                email: supervisor_id,
                first_name: "",
            });
        }
    }

    await store.queueEmail(clinician_email, OutboundEmailStatus.PENDING);
    await store.addEmailReminder({
        ...clinician_email,
        id: v4(),
        next_send_time: moment().add(48, "hour").startOf("day").toISOString(),
        max_reminder_count: 3,
        current_reminder_count: 0,
        is_completed: false,
    });
}
